import { useCallback, useEffect, useState, useRef } from 'react';
import { useSessionExpiration } from 'src/hooks/useSessionExpiration';
import { useUserInactivity } from 'src/hooks/useUserInactivity';
import {
  SessionExpiredOverlay,
  useSessionExpiredOverlay,
  isSessionExpiredInStorage,
  clearSessionExpiredFlag,
} from 'src/components/SessionExpiredOverlay';
import { useAuth } from 'src/auth/useAuth';
import { isAuthDisabled } from 'src/auth/authConfig';
import { clearEntraTokenCache } from 'src/services/utils/authTokenHttp';
import { useUserInactivityStore } from 'src/stores/useUserInactivityStore';
import {
  captureSessionException,
  captureSessionMessage,
} from 'src/utils/sentry/sessionSentry';

/**
 * Format remaining seconds as MM:SS
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export interface SessionExpirationWarningProps {
  /** Optional callback when session is extended */
  readonly onSessionExtended?: () => void;
  /** Optional callback when session expire */
  readonly onSessionExpired?: () => void;
}

const INACTIVITY_GRACE_PERIOD_MS = 60_000;

/**
 * Session Expiration Warning Component
 *
 * Monitors backend session expiration (not token expiration) and shows a warning
 * popup when the session is about to expire. Provides option to extend session.
 *
 * This works for both Keycloak and Entra ID authentication flows.
 */
export function SessionExpirationWarning({
  onSessionExtended,
  onSessionExpired,
}: SessionExpirationWarningProps) {
  const auth = useAuth();
  const authDisabled = isAuthDisabled();

  const {
    isInactivityWarningVisible,
    resetInactivity,
    inactivityTimeoutMinutes,
  } = useUserInactivity();

  const {
    isExpiringSoon,
    remainingSeconds,
    isSessionActive,
    isLoaded,
    refreshSession,
  } = useSessionExpiration();

  const { isSessionExpiredVisible, showSessionExpired, hideSessionExpired } =
    useSessionExpiredOverlay();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const prevExpiresAtRef = useRef<number | null>(null);
  const hasCheckedStorageRef = useRef(false);
  const wasExpiringSoonRef = useRef(false);
  const inactivityGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const previousShowWarningRef = useRef(false);

  const logout = useCallback(async () => {
    try {
      await auth.actions.logout();
    } catch (error) {
      captureSessionException(error, { event: 'session.logout.failed' });
      showSessionExpired();
    }
  }, [auth.actions, showSessionExpired]);

  // On mount: if session was previously marked as expired (before a page refresh),
  // clear the flag and immediately redirect to login.
  useEffect(() => {
    if (hasCheckedStorageRef.current) return;
    hasCheckedStorageRef.current = true;

    if (!authDisabled && auth.authenticated && isSessionExpiredInStorage()) {
      console.log(
        '🚨 SessionExpirationWarning: Session expired flag found after page refresh — redirecting to login',
      );
      captureSessionMessage(
        'Session expired flag found after refresh -> redirecting',
        'warning',
        { event: 'session.expiration.flagFoundAfterRefresh' },
      );
      clearSessionExpiredFlag();
      clearEntraTokenCache();
      void logout();
    }
  }, [authDisabled, auth.authenticated, logout]);

  // Reset dismissed state when session expiration changes (session was extended)
  useEffect(() => {
    if (remainingSeconds > 0 && prevExpiresAtRef.current !== null) {
      if (remainingSeconds > prevExpiresAtRef.current + 60) {
        setIsDismissed(false);
      }
    }
    prevExpiresAtRef.current = remainingSeconds;
  }, [remainingSeconds]);

  // Track when session was previously "expiring soon" so we can detect the
  // transition to remainingSeconds === 0 (the two conditions are mutually
  // exclusive in a single render because isExpiringSoon requires remainingSeconds > 0).
  useEffect(() => {
    if (isExpiringSoon) {
      wasExpiringSoonRef.current = true;
    }
  }, [isExpiringSoon]);

  // Handle backend session expiration (countdown reached 0).
  // Uses wasExpiringSoonRef to bridge the gap: isExpiringSoon is false when
  // remainingSeconds === 0, but we know the session *was* expiring.
  useEffect(() => {
    if (
      isLoaded &&
      remainingSeconds === 0 &&
      wasExpiringSoonRef.current &&
      !isSessionExpiredVisible
    ) {
      console.log(
        '🚨 SessionExpirationWarning: Backend session expired (countdown reached 0)',
      );
      wasExpiringSoonRef.current = false;
      captureSessionMessage('Backend session expired', 'warning', {
        event: 'session.expiration.backendExpired',
      });
      showSessionExpired();
      onSessionExpired?.();
    }
  }, [
    isLoaded,
    remainingSeconds,
    isSessionExpiredVisible,
    showSessionExpired,
    onSessionExpired,
  ]);

  // Handle auto-pausing token refresh when warning is visible
  const { setForcePauseTokenRefresh } = useUserInactivityStore(
    (s) => s.actions,
  );

  const showWarning =
    !isDismissed &&
    !isSessionExpiredVisible &&
    (isInactivityWarningVisible ||
      (isLoaded &&
        isExpiringSoon &&
        remainingSeconds > 0 &&
        remainingSeconds <= 60 &&
        isSessionActive));

  useEffect(() => {
    if (showWarning && !previousShowWarningRef.current) {
      captureSessionMessage('Session warning popup shown', 'warning', {
        event: 'session.warning.popup.shown',
        extra: {
          source: isInactivityWarningVisible ? 'inactivity' : 'backend-session',
          remainingSeconds,
          isLoaded,
          isExpiringSoon,
          isSessionActive,
          inactivityTimeoutMinutes,
        },
      });
    }

    if (!showWarning && previousShowWarningRef.current) {
      captureSessionMessage('Session warning popup hidden', 'info', {
        event: 'session.warning.popup.hidden',
        extra: {
          dismissed: isDismissed,
          sessionExpiredVisible: isSessionExpiredVisible,
          remainingSeconds,
          isLoaded,
          isExpiringSoon,
          isSessionActive,
        },
      });
    }

    previousShowWarningRef.current = showWarning;
  }, [
    showWarning,
    isInactivityWarningVisible,
    remainingSeconds,
    isLoaded,
    isExpiringSoon,
    isSessionActive,
    inactivityTimeoutMinutes,
    isDismissed,
    isSessionExpiredVisible,
  ]);

  useEffect(() => {
    if (showWarning) {
      setForcePauseTokenRefresh(true);
    } else {
      setForcePauseTokenRefresh(false);
    }
    return () => setForcePauseTokenRefresh(false);
  }, [showWarning, setForcePauseTokenRefresh]);

  // Inactivity escalation: when the inactivity warning is visible and the user
  // does not respond within a 60-second grace period, show the session expired overlay.
  // This ensures magic-link and other auth types that lack a backend session
  // countdown still get the expired overlay after prolonged inactivity.
  useEffect(() => {
    if (
      isInactivityWarningVisible &&
      !isDismissed &&
      !isSessionExpiredVisible
    ) {
      captureSessionMessage('Starting inactivity grace timer', 'warning', {
        event: 'session.inactivity.graceTimer.started',
        extra: {
          gracePeriodMs: INACTIVITY_GRACE_PERIOD_MS,
          inactivityTimeoutMinutes,
        },
      });
      inactivityGraceTimerRef.current = setTimeout(() => {
        console.log(
          '🚨 SessionExpirationWarning: Inactivity grace period elapsed — showing session expired overlay',
        );
        captureSessionMessage(
          'Inactivity grace period elapsed -> showing session expired overlay',
          'warning',
          { event: 'session.expiration.inactivityGraceElapsed' },
        );
        showSessionExpired();
        onSessionExpired?.();
      }, INACTIVITY_GRACE_PERIOD_MS);
    }

    return () => {
      if (inactivityGraceTimerRef.current) {
        captureSessionMessage('Clearing inactivity grace timer', 'info', {
          event: 'session.inactivity.graceTimer.cleared',
        });
        clearTimeout(inactivityGraceTimerRef.current);
        inactivityGraceTimerRef.current = null;
      }
    };
  }, [
    isInactivityWarningVisible,
    isDismissed,
    isSessionExpiredVisible,
    showSessionExpired,
    onSessionExpired,
    inactivityTimeoutMinutes,
  ]);

  // Handle extend session click (includes Entra token refresh when continuing after inactivity)
  const handleExtendSession = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    console.log('🔄 SessionExpirationWarning: Extending session...');
    captureSessionMessage('Extending session (user action)', 'info', {
      event: 'session.extend.requested',
    });

    try {
      setForcePauseTokenRefresh(false);
      if (isInactivityWarningVisible) {
        resetInactivity();
      }
      // Force token refresh across all active providers (Entra, Magic Link, Keycloak)
      await auth.actions.updateToken(true);
      clearEntraTokenCache();

      await refreshSession();
      setIsDismissed(true);
      onSessionExtended?.();
      console.log('✅ SessionExpirationWarning: Session extended successfully');

      captureSessionMessage('Session extended successfully', 'info', {
        event: 'session.extend.succeeded',
      });
    } catch (error) {
      console.error(
        '❌ SessionExpirationWarning: Failed to extend session:',
        error,
      );
      captureSessionException(error, {
        event: 'session.extend.failed',
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [
    isRefreshing,
    isInactivityWarningVisible,
    resetInactivity,
    auth.actions,
    refreshSession,
    onSessionExtended,
    setForcePauseTokenRefresh,
  ]);

  // Handle dismiss click – log out when user dismisses the session warning
  const handleDismiss = useCallback(async () => {
    setIsDismissed(true);
    setForcePauseTokenRefresh(false);
    captureSessionMessage(
      'Dismissed session expiration warning -> logging out',
      'warning',
      {
        event: 'session.dismissed.warningLoggedOut',
      },
    );
    await logout();
  }, [logout, setForcePauseTokenRefresh]);

  // Handle successful token refresh from overlay
  const handleTokenRefreshSuccess = useCallback(() => {
    hideSessionExpired('refresh');
    setIsDismissed(false);
    setForcePauseTokenRefresh(false);

    captureSessionMessage(
      'Token refresh succeeded while session expired overlay visible',
      'info',
      {
        event: 'session.expiration.tokenRefreshSuccess',
      },
    );
  }, [hideSessionExpired, setForcePauseTokenRefresh]);

  // Don't render if auth is disabled or user not authenticated.
  // We do not require isLoaded here – the inactivity warning works without the session API.
  // Backend session warning is only shown when isLoaded is true (see showWarning below).
  if (authDisabled || !auth.authenticated) {
    return null;
  }

  return (
    <>
      {/* Session Expired Overlay */}
      <SessionExpiredOverlay
        isVisible={isSessionExpiredVisible}
        onLoginAgain={hideSessionExpired}
        onTokenRefreshSuccess={handleTokenRefreshSuccess}
        customMessage="Your session has expired due to inactivity."
      />

      {/* Session Expiration Warning Popup */}
      {showWarning && (
        <div
          className="fixed bottom-4 right-4 z-40 max-w-sm bg-white rounded-lg shadow-lg border border-amber-200 overflow-hidden"
          role="alert"
          aria-live="polite"
          data-inactivity-warning="true"
        >
          {/* Header */}
          <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg
                  className="h-5 w-5 text-amber-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
                <h3 className="text-sm font-semibold text-amber-800">
                  Session Expiring Soon
                </h3>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-amber-600 hover:text-amber-800 transition-colors"
                aria-label="Dismiss"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-3">
            <p className="text-sm text-gray-600 mb-3">
              {isInactivityWarningVisible ? (
                <>
                  You have been inactive for{' '}
                  <span className="font-semibold text-amber-700">
                    {inactivityTimeoutMinutes} minutes
                  </span>{' '}
                  Click below to continue your session.
                </>
              ) : (
                <>
                  Your session will expire in{' '}
                  <span className="font-semibold text-amber-700">
                    {formatTime(remainingSeconds)}
                  </span>{' '}
                  Click below to continue working.
                </>
              )}
            </p>

            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleExtendSession}
                disabled={isRefreshing}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  isRefreshing
                    ? 'bg-blue-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                }`}
              >
                {isRefreshing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Extending...
                  </span>
                ) : (
                  'Continue Session'
                )}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SessionExpirationWarning;
